import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";

import ChartNavigation from "src/components/ChartNavigation";
import useChartNavigation from "src/hooks/useChartNavigation";
import {
  centerWindow,
  ChartCard,
  ChartToggle,
  CHART_COLORS,
  createGradientAreaVisual,
  METRIC_OPTIONS,
  resolveVisibleCount,
  TREND_AREA_GRADIENT,
} from "../charts";
import { useHoursWorkedTrend } from "../../hooks";
import { getCurrentCalendarPeriod } from "../../utils/dateRangeHelper";
import "./HoursWorkedTrendChart.scss";

/*
 * Hours Worked Trend (graph2). A smooth single-series area chart; the Hours /
 * Amount and Month / Quarter / Year strips are both request parameters, so
 * either one refetches.
 */

const CHART_STYLE = { height: "270px", width: "100%" };

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/*
 * Value-axis labels for the Amount metric. Real amounts run from 0 into the
 * low thousands, so abbreviating unconditionally would render 0 / 922 / 4508
 * as "0k" / "1k" / "5k"; mirrors the mobile chart's yAxisLabelFormatter.
 */
const compactAmount = (value) => {
  const safe = Number(value) || 0;
  return safe >= 1000 ? `${(safe / 1000).toFixed(1)}k` : `${safe}`;
};

/* 80.5 -> "80h 30m", matching the mobile hour/minute formatting. */
export const formatHours = (value) => {
  const safe = Number(value) || 0;
  const hours = Math.floor(safe);
  const minutes = Math.round((safe - hours) * 60);

  // Rounding 59.6 minutes up has to carry into the hour.
  if (minutes === 60) return `${hours + 1}h 00m`;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

const HIDDEN_GRID = { visible: false };
const HIDDEN_LINE = { visible: false };
const AXIS_LINE = { color: CHART_COLORS.axisLine };

const MARKERS = {
  visible: true,
  size: 7,
  background: CHART_COLORS.trendLine,
  border: { color: "#ffffff", width: 2 },
};

const CROSSHAIR = {
  visible: true,
  color: CHART_COLORS.gridLine,
  width: 1,
};

const TOOLTIP_CONFIG = {
  visible: true,
  background: "#333333",
  color: "#ffffff",
  font: "12px Poppins, sans-serif",
  border: { width: 0 },
  padding: { top: 8, right: 12, bottom: 8, left: 12 },
};

const HoursWorkedTrendChart = ({ providerId, periodTypes, scope }) => {
  const capitalizeFirstLetter = (value) =>
    typeof value === "string" && value.length ? value[0].toUpperCase() + value.slice(1) : value;

  const [selectedYear, setSelectedYear] = useState(null);

  const periodOptions = useMemo(
    () =>
      Array.isArray(periodTypes?.periodOptions)
        ? periodTypes.periodOptions.map((row) => ({
            value: row.period_type_mstr_key,
            label: capitalizeFirstLetter(row.period_type_name),
          }))
        : [],
    [periodTypes?.periodOptions]
  );

  const { metric, setMetric, granularity, setGranularity, chartData, calendarPeriods, total, isAmount, isLoading } =
    useHoursWorkedTrend(
      providerId,
      periodTypes?.selectedPeriodType?.id,
      selectedYear?.text,
      periodTypes?.periodOptions || [],
      scope
    );

  const yearOptions = useMemo(() => {
    return calendarPeriods.map((item) => {
      return {
        text: item.period_name,
        value: item.period_mstr_key,
      };
    });
  }, [calendarPeriods]);

  useEffect(() => {
    if (yearOptions.length > 0) {
      const selectedPeriodOption = periodOptions.find((option) => option.value === granularity);
      const unit = String(selectedPeriodOption?.label ?? "").toLowerCase();
      const currentPeriod = getCurrentCalendarPeriod(calendarPeriods, unit);
      setSelectedYear(yearOptions.find((option) => option.value === currentPeriod?.period_mstr_key) || yearOptions[0]);
    }
  }, [calendarPeriods, granularity, periodOptions, yearOptions]);

  const formatValue = useCallback((value) => (isAmount ? currency.format(value || 0) : formatHours(value)), [isAmount]);

  const categories = useMemo(() => chartData.map((point) => point.category), [chartData]);

  const plotRef = useRef(null);
  const [plotWidth, setPlotWidth] = useState(0);

  useEffect(() => {
    const element = plotRef.current;
    if (!element) return undefined;

    const updateWidth = () => setPlotWidth(element.clientWidth);
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  /*
   * Same fixed-pitch windowing as the Earnings Overview chart, so both cards
   * show the same number of periods at once instead of this chart's own
   * width/50 count (which had no upper bound and could show far more or
   * fewer periods than Earnings for the same card width).
   */
  const visibleCount = useMemo(() => resolveVisibleCount(plotWidth), [plotWidth]);

  const nav = useChartNavigation(0, visibleCount, categories);
  const { setRange } = nav;

  /*
   * calendarPeriods' period_name and the trend's period_label aren't always
   * identical strings (e.g. "2025" vs "Jul 2025"), so the match is fuzzy in
   * both directions - same as handlePlotAreaClick below.
   */
  const highlightedIndex = useMemo(() => {
    if (!selectedYear?.text || !categories.length) return -1;
    return categories.findIndex(
      (cat) => cat.includes(selectedYear.text) || selectedYear.text.includes(cat)
    );
  }, [categories, selectedYear]);

  /* Re-centre on the selected period, mirroring Earnings Overview. */
  useEffect(() => {
    if (highlightedIndex < 0 || !categories.length) return;
    setRange(centerWindow(highlightedIndex, visibleCount, categories.length));
  }, [highlightedIndex, visibleCount, categories.length, setRange]);

  const chartArea = useMemo(
    () => ({
      margin: { top: 12, right: 12, bottom: 4, left: 4 },
      background: isAmount ? CHART_COLORS.amountBackground : CHART_COLORS.hoursBackground,
    }),
    [isAmount]
  );

  const areaVisual = useMemo(() => createGradientAreaVisual({ stops: TREND_AREA_GRADIENT }), []);

  const valueAxisLabels = useMemo(
    () => ({
      font: "12px Poppins, sans-serif",
      color: CHART_COLORS.mutedLabel,
      content: (e) => (isAmount ? compactAmount(e.value) : `${e.value}`),
    }),
    [isAmount]
  );

  const categoryLabels = useMemo(
    () => ({
      font: "12px Poppins, sans-serif",
      color: CHART_COLORS.mutedLabel,
      margin: { top: 8 },
    }),
    []
  );

  const handleYearChange = useCallback(
    (value) => {
      const year = yearOptions.find((y) => y.value === value.value);
      if (year) setSelectedYear(year);
    },
    [yearOptions]
  );

  /*
   * Lets clicking a point on the trend line select that period too, the same
   * way a bar click does on the Earnings Overview chart - including a period
   * with a zero value, since onPlotAreaClick fires from the category's column
   * regardless of what the line/area actually draws there. calendarPeriods'
   * period_name and the trend's period_label aren't always identical strings
   * (e.g. "2025" vs "Jul 2025"), so the match is fuzzy in both directions.
   */
  const handlePlotAreaClick = useCallback(
    (e) => {
      if (!e.category) return;

      const match = yearOptions.find(
        (option) => e.category.includes(option.text) || option.text.includes(e.category)
      );
      if (match) setSelectedYear(match);
    },
    [yearOptions]
  );

  const renderTooltip = useCallback(
    ({ point }) => (
      <div className="hours-trend__tooltip">
        <span className="hours-trend__tooltip-label">{point?.category}</span>
        <span className="hours-trend__tooltip-value">
          {isAmount ? "Amount" : "Hours"} : {formatValue(point?.value)}
        </span>
      </div>
    ),
    [isAmount, formatValue]
  );

  const toggles = (
    <>
      <ChartToggle options={METRIC_OPTIONS} value={metric} onChange={setMetric} ariaLabel="Trend metric" />
      {periodOptions.length > 0 && (
        <ChartToggle options={periodOptions} value={granularity} onChange={setGranularity} ariaLabel="Trend period" />
      )}
    </>
  );

  const currentPeriodLabel = periodOptions.find(opt => opt.value === granularity)?.label || "Period";

  return (
    <ChartCard
      className="hours-worked-trend"
      title="Hours Worked Trend"
      subtitle={`${formatValue(total)} logged`}
      toggles={toggles}
      yearOptions={yearOptions}
      selectedYear={selectedYear}
      onYearChange={handleYearChange}
      periodTypeLabel={currentPeriodLabel}
      isLoading={isLoading}
      isEmpty={!chartData.length}
    >
      <div
        className="hours-trend__plot"
        ref={plotRef}
        style={{
          backgroundColor: isAmount ? CHART_COLORS.amountBackground : CHART_COLORS.hoursBackground,
        }}
      >
        <ChartNavigation
          min={nav.range.min}
          max={nav.range.max}
          totalItems={categories.length}
          onNavigateLeft={nav.navigateLeft}
          onNavigateRight={nav.navigateRight}
          onReset={nav.resetRange}
        />

        <Chart
          key={`trend-${nav.range.min}-${nav.range.max}`}
          style={CHART_STYLE}
          chartArea={chartArea}
          renderAs="svg"
          transitions={false}
          pannable={false}
          zoomable={false}
          onPlotAreaClick={handlePlotAreaClick}
        >
          <ChartLegend visible={false} />
          <ChartTooltip render={renderTooltip} {...TOOLTIP_CONFIG} />

          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              categories={categories}
              min={nav.range.min}
              max={nav.range.max}
              labels={categoryLabels}
              majorGridLines={HIDDEN_GRID}
              line={HIDDEN_LINE}
              crosshair={CROSSHAIR}
            />
          </ChartCategoryAxis>

          <ChartValueAxis>
            <ChartValueAxisItem labels={valueAxisLabels} majorGridLines={HIDDEN_GRID} line={AXIS_LINE} />
          </ChartValueAxis>

          <ChartSeries>
            <ChartSeriesItem
              type="area"
              data={chartData}
              field="value"
              categoryField="category"
              color={isAmount ? CHART_COLORS.amountBackground : CHART_COLORS.hoursBackground}
              line={{ width: 2, style: "smooth" }}
              markers={MARKERS}
              visual={areaVisual}
            />
          </ChartSeries>
        </Chart>
      </div>
    </ChartCard>
  );
};

export default memo(HoursWorkedTrendChart);
